import React, { useState, useEffect } from "react";
import { api, euro, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ServicePageHeader } from "@/components/ServiceHistoryButton";
import { AdminCost, useSourceCosts } from "@/components/AdminSourceCost";
import { toast } from "sonner";
import { ConfirmButton } from "@/components/ConfirmButton";
import { PackageCheck, Upload, FileText, CheckCircle2 } from "lucide-react";
import { DocumentAutofill, mapDocToAnagrafica } from "@/components/DocumentAutofill";
import { AddressFields, customerAddress } from "@/components/AddressFields";
import { CustomerPicker } from "@/components/CustomerPicker";

const P = { nome: "", indirizzo: "", cap: "", citta: "", provincia: "", email: "", telefono: "" };

function PartyFields({ obj, setObj, prefix }) {
  return (
    <div className="space-y-3">
      <div><Label>Nome / Ragione sociale *</Label><Input className="mt-1.5" data-testid={`${prefix}-nome`} value={obj.nome} onChange={(e) => setObj({ nome: e.target.value })} /></div>
      <div><Label>Indirizzo (es. Via Cariati 43) *</Label><Input className="mt-1.5" data-testid={`${prefix}-indirizzo`} value={obj.indirizzo} onChange={(e) => setObj({ indirizzo: e.target.value })} placeholder="Via/Piazza + nome + civico" /></div>
      <AddressFields prefix={prefix} showAddress={false} required
        values={{ cap: obj.cap, city: obj.citta, province: obj.provincia }}
        onChange={(p) => { const out = {}; if ("cap" in p) out.cap = p.cap; if ("city" in p) out.citta = p.city; if ("province" in p) out.provincia = p.province; setObj(out); }} />
      <div className="grid sm:grid-cols-2 gap-3">
        <div><Label>Email *</Label><Input type="email" className="mt-1.5" data-testid={`${prefix}-email`} value={obj.email} onChange={(e) => setObj({ email: e.target.value })} /></div>
        <div><Label>Telefono</Label><Input type="tel" className="mt-1.5" data-testid={`${prefix}-tel`} value={obj.telefono} onChange={(e) => setObj({ telefono: e.target.value })} /></div>
      </div>
    </div>
  );
}

export default function Raccomandata() {
  const [mitt, setMitt] = useState({ ...P });
  const [customerId, setCustomerId] = useState("");
  const setMittPatch = (patch) => setMitt((m) => ({ ...m, ...patch }));
  const setDestPatch = (patch) => setDest((d) => ({ ...d, ...patch }));
  const pickCustomer = (c) => {
    setCustomerId(c.id);
    const a = customerAddress(c);
    setMitt((m) => ({ ...m, nome: c.name || m.nome, email: c.email || m.email, indirizzo: a.address || m.indirizzo, cap: a.cap || m.cap, citta: a.city || m.citta, provincia: a.province || m.provincia, telefono: c.phone || m.telefono }));
  };
  const [dest, setDest] = useState({ ...P });
  const [ar, setAr] = useState(true);
  const [file, setFile] = useState({ base64: null, name: null });
  const [quote, setQuote] = useState(null);
  const [tracking, setTracking] = useState(null);
  const [busy, setBusy] = useState(false);
  const [svcFee, setSvcFee] = useState(null);
  const { costs: sourceCosts } = useSourceCosts();
  useEffect(() => { api.get("/openapi/service-prices").then((r) => setSvcFee(r.data?.oa_postal_raccomandata?.price ?? null)).catch(() => {}); }, []);
  const [requireDoc, setRequireDoc] = useState(false);
  const [docUploaded, setDocUploaded] = useState(false);
  useEffect(() => { api.get("/settings/features").then((r) => setRequireDoc(!!r.data.require_doc_raccomandata)).catch(() => {}); }, []);
  const onDocData = (d) => { const a = mapDocToAnagrafica(d); setMitt((m) => ({ ...m, nome: [a.nome, a.cognome].filter(Boolean).join(" ") || m.nome })); };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 6 * 1024 * 1024) { toast.error("Il documento supera 6 MB"); return; }
    const r = new FileReader();
    r.onload = () => setFile({ base64: r.result, name: f.name });
    r.readAsDataURL(f);
  };

  const doQuote = async () => {
    if (requireDoc && !docUploaded) { toast.error("Carica il documento del cliente per procedere"); return; }
    if (!mitt.nome || !mitt.indirizzo || !mitt.cap || !mitt.citta || !mitt.provincia || !mitt.email) { toast.error("Completa i dati del mittente (indirizzo, CAP, città, provincia, email)"); return; }
    if (!dest.nome || !dest.indirizzo || !dest.cap || !dest.citta || !dest.provincia || !dest.email) { toast.error("Completa i dati del destinatario (indirizzo, CAP, città, provincia, email)"); return; }
    if (!file.base64) { toast.error("Carica il documento PDF da spedire"); return; }
    setBusy(true); setTracking(null);
    try {
      const { data } = await api.post("/openapi/raccomandata/quote", { mittente: mitt, destinatario: dest, document_base64: file.base64, document_filename: file.name, ar });
      setQuote(data);
      if (data.history_document_notice) toast.warning(data.history_document_notice);
      else toast.success(`Preventivo: ${data.price != null ? euro(data.price) : "ok"}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore preventivo"); }
    finally { setBusy(false); }
  };

  const doConfirm = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/openapi/raccomandata/${quote.provider_id}/confirm`);
      setTracking(data);
      toast.success("Raccomandata confermata e spedita!");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore conferma"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6 max-w-2xl" data-testid="raccomandata-page">
      <ServicePageHeader service="raccomandata">
        <p className="text-xs font-medium tracking-wider uppercase text-muted-foreground">Servizi pubblici</p>
        <h1 className="font-head text-3xl font-bold tracking-tight mt-1 flex items-center gap-2"><PackageCheck className="w-7 h-7 text-primary" /> Raccomandata A/R online</h1>
        <p className="text-sm text-muted-foreground mt-1">Carica il documento, ottieni il preventivo, conferma la spedizione e segui il tracking.</p>
      </ServicePageHeader>
      {svcFee != null && <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 text-sm" data-testid="racc-fee">Commissione servizio: <strong>{euro(svcFee)}</strong> · <span className="text-muted-foreground">servizio una tantum per invio</span> · oltre al costo di spedizione, scalata dal Plafond servizi alla conferma. <AdminCost cost={sourceCosts?.openapi?.oa_postal_raccomandata} note="commissione, escluso costo postale" testId="racc-source-cost" /></div>}

      {!tracking && (
        <Card className="p-6 glow-border space-y-5" data-testid="raccomandata-form">
          <DocumentAutofill tipoCliente="privato" required={requireDoc} onData={onDocData} onUploadedChange={setDocUploaded} />
          <CustomerPicker value={customerId} onPick={pickCustomer} placeholder="Seleziona un cliente salvato per compilare il mittente" testid="racc-customer-select" />
          <div><h3 className="font-head font-semibold mb-2">Mittente</h3><PartyFields obj={mitt} setObj={setMittPatch} prefix="racc-mitt" /></div>
          <div><h3 className="font-head font-semibold mb-2">Destinatario</h3><PartyFields obj={dest} setObj={setDestPatch} prefix="racc-dest" /></div>
          <div>
            <Label className="flex items-center gap-2"><Upload className="w-4 h-4" /> Documento da spedire (PDF) *</Label>
            <Input type="file" accept=".pdf" className="mt-1.5" data-testid="racc-file" onChange={onFile} />
            {file.name && <Badge variant="outline" className="mt-2 gap-1"><FileText className="w-3 h-3" /> {file.name}</Badge>}
          </div>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={ar} onChange={(e) => setAr(e.target.checked)} data-testid="racc-ar" /> Con avviso di ricevimento (A/R)</label>
          {!quote ? (
            <Button next onClick={doQuote} disabled={busy || (requireDoc && !docUploaded)} data-testid="racc-quote-btn">{busy ? "Calcolo…" : "Calcola preventivo"}</Button>
          ) : (
            <div className="rounded-lg border border-primary/40 p-4 space-y-3">
              {quote.history_document_notice && <p role="status" className="text-sm text-amber-700 dark:text-amber-400" data-testid="raccomandata-history-document-notice">{quote.history_document_notice}</p>}
              <p className="text-sm">Costo spedizione (IVA 22% inclusa): <strong>{quote.price != null ? euro(quote.price) : "—"}</strong> + commissione servizio.</p>
              <ConfirmButton onConfirm={doConfirm} disabled={busy} data-testid="racc-confirm-btn" title="Confermi la spedizione della raccomandata?" description={`Verrà confermata e spedita la raccomandata${quote?.price != null ? ` (costo ${euro(quote.price)} IVA inclusa + commissione)` : ""}. L'operazione è irreversibile.`} confirmText="Conferma e spedisci">{busy ? "Invio…" : "Conferma e spedisci"}</ConfirmButton>
            </div>
          )}
        </Card>
      )}

      {tracking && (
        <Card className="p-6 glow-border space-y-3 text-center" data-testid="raccomandata-result">
          <CheckCircle2 className="w-12 h-12 text-teal-500 mx-auto" />
          <h2 className="font-head text-xl font-bold">Raccomandata spedita</h2>
          {tracking.tracking ? <p className="text-sm text-muted-foreground" data-testid="raccomandata-tracking-code">Codice di tracciamento: <span className="font-mono">{tracking.tracking}</span></p> : <div className="space-y-1 text-sm text-muted-foreground" data-testid="raccomandata-tracking-pending"><p>Il codice di tracciamento postale non è ancora disponibile.</p><p>Riferimento pratica: <span className="font-mono">{tracking.provider_id}</span></p></div>}
          <p className="text-sm">Addebitato: <strong>{euro(tracking.total)}</strong></p>
          <Button variant="ghost" onClick={() => { setTracking(null); setQuote(null); setFile({ base64: null, name: null }); setMitt({ ...P }); setDest({ ...P }); }} data-testid="racc-new">Nuova raccomandata</Button>
        </Card>
      )}
    </div>
  );
}
